import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  TextField,
  Alert,
  Snackbar,
  TablePagination,
  InputAdornment,
  Checkbox,
  Chip,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import {
  locationService,
  storeLocationMappingService,
} from '../services'
import { formatDateTime } from '../utils/dateFormat'

const SEARCH_DEBOUNCE_MS = 450

function useDebouncedValue(value, ms) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

/** Edit linked locations for a fixed organization (replace API). */
function EditOrgLocationsDialog({ open, onClose, organizationId, organizationName, onSaved }) {
  const [locSearch, setLocSearch] = useState('')
  const debouncedLocSearch = useDebouncedValue(locSearch, SEARCH_DEBOUNCE_MS)
  const [locPage, setLocPage] = useState(0)
  const [locRowsPerPage, setLocRowsPerPage] = useState(10)
  const [locTotal, setLocTotal] = useState(0)
  const [locPageItems, setLocPageItems] = useState([])
  const [locLoading, setLocLoading] = useState(false)
  const [selectedLocations, setSelectedLocations] = useState(() => new Map())
  const [actionError, setActionError] = useState(null)
  const [saveLoading, setSaveLoading] = useState(false)
  const [seedLoading, setSeedLoading] = useState(false)

  useEffect(() => {
    if (!open || !organizationId) return
    setLocSearch('')
    setLocPage(0)
    setActionError(null)
    let cancelled = false
    ;(async () => {
      setSeedLoading(true)
      try {
        const data = await storeLocationMappingService.getOrgLocationLinksAll(organizationId)
        if (cancelled) return
        const m = new Map()
        for (const item of data.items || []) {
          const loc = item.location
          if (loc?.id) m.set(loc.id, { id: loc.id, name: loc.name, code: loc.code })
        }
        setSelectedLocations(m)
      } catch (e) {
        if (!cancelled) {
          const msg =
            e.response?.data?.message ||
            (typeof e.response?.data === 'string' ? e.response.data : null) ||
            e.message ||
            'Failed to load current links'
          setActionError(Array.isArray(msg) ? msg.join(', ') : String(msg))
          setSelectedLocations(new Map())
        }
      } finally {
        if (!cancelled) setSeedLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, organizationId])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      try {
        setLocLoading(true)
        const q = debouncedLocSearch.trim()
        const data = await locationService.getLocations({
          page: locPage + 1,
          limit: locRowsPerPage,
          ...(q ? { search: q } : {}),
        })
        if (cancelled) return
        setLocPageItems(data?.items || [])
        setLocTotal(data?.pagination?.total ?? 0)
      } catch {
        if (!cancelled) {
          setLocPageItems([])
          setLocTotal(0)
        }
      } finally {
        if (!cancelled) setLocLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, locPage, locRowsPerPage, debouncedLocSearch])

  useEffect(() => {
    if (!open) return
    setLocPage(0)
  }, [debouncedLocSearch, open])

  const toggleLoc = useCallback((loc, checked) => {
    setSelectedLocations((prev) => {
      const next = new Map(prev)
      if (checked) next.set(loc.id, { id: loc.id, name: loc.name, code: loc.code })
      else next.delete(loc.id)
      return next
    })
  }, [])

  const removeChip = useCallback((id) => {
    setSelectedLocations((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const handleSave = async () => {
    setActionError(null)
    const locationIds = Array.from(selectedLocations.keys())
    try {
      setSaveLoading(true)
      const res = await storeLocationMappingService.replaceOrgLocations(organizationId, {
        location_ids: locationIds,
      })
      const msg = `Updated: +${res.added ?? 0} / −${res.removed ?? 0}${
        res.unchanged != null ? ` (unchanged ${res.unchanged})` : ''
      }`
      onSaved?.(msg)
      onClose()
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        (typeof e.response?.data === 'string' ? e.response.data : null) ||
        e.message ||
        'Save failed'
      setActionError(Array.isArray(msg) ? msg.join(', ') : String(msg))
    } finally {
      setSaveLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>Edit locations</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          Shop Id
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          {organizationName || `Shop Id #${organizationId}`}
        </Typography>
        {seedLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Linked locations
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Saving replaces the full set for this shop. You may remove all links by clearing every
          selection.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2, minHeight: 32 }}>
          {selectedLocations.size === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None selected (all links will be removed)
            </Typography>
          ) : (
            Array.from(selectedLocations.values()).map((loc) => (
              <Chip
                key={loc.id}
                label={`${loc.name} (${loc.code})`}
                size="small"
                onDelete={() => removeChip(loc.id)}
              />
            ))
          )}
        </Box>
        <TextField
          fullWidth
          size="small"
          label="Search locations"
          value={locSearch}
          onChange={(e) => setLocSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666' }} fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Paper variant="outlined" sx={{ p: 1, maxHeight: 280, overflow: 'auto' }}>
          {locLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : locPageItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No locations match this search.
            </Typography>
          ) : (
            locPageItems.map((loc) => {
              const checked = selectedLocations.has(loc.id)
              return (
                <FormControlLabel
                  key={loc.id}
                  sx={{ display: 'flex', ml: 0, py: 0.25, px: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={(e) => toggleLoc(loc, e.target.checked)}
                    />
                  }
                  label={`${loc.name} (${loc.code})`}
                />
              )
            })
          )}
        </Paper>
        <TablePagination
          component="div"
          count={locTotal}
          page={locPage}
          onPageChange={(_, p) => setLocPage(p)}
          rowsPerPage={locRowsPerPage}
          onRowsPerPageChange={(e) => {
            setLocRowsPerPage(parseInt(e.target.value, 10))
            setLocPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={saveLoading || seedLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saveLoading || seedLoading}
          sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#cc0000' } }}
        >
          {saveLoading ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const StoreLocationMappingOrgDetailPage = () => {
  const { orgId: orgIdParam } = useParams()
  const navigate = useNavigate()
  const orgId = orgIdParam ? parseInt(orgIdParam, 10) : NaN

  const [org, setOrg] = useState(null)
  const [linkItems, setLinkItems] = useState([])
  const [totalLinks, setTotalLinks] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [editOpen, setEditOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  useEffect(() => {
    setPage(0)
  }, [orgId])

  useEffect(() => {
    if (totalLinks === 0) {
      setPage(0)
      return
    }
    const maxPage = Math.max(0, Math.ceil(totalLinks / rowsPerPage) - 1)
    setPage((p) => (p > maxPage ? maxPage : p))
  }, [totalLinks, rowsPerPage])

  const orgName =
    org?.organization_name ?? org?.name ?? (Number.isFinite(orgId) ? `Shop Id #${orgId}` : '')

  const fetchLinksPage = useCallback(async () => {
    if (!Number.isFinite(orgId) || orgId < 1) {
      setLoadError('Invalid Shop Id')
      setLoading(false)
      setLinkItems([])
      setTotalLinks(0)
      return
    }
    setLoadError(null)
    setLoading(true)
    try {
      const linksData = await storeLocationMappingService.getOrgLocationLinks(orgId, {
        page: page + 1,
        limit: rowsPerPage,
      })
      setOrg(linksData?.organization || null)
      setLinkItems(linksData?.items || [])
      setTotalLinks(linksData?.pagination?.total ?? 0)
    } catch (e) {
      setLoadError(e.response?.data?.message || e.message || 'Failed to load')
      setOrg(null)
      setLinkItems([])
      setTotalLinks(0)
    } finally {
      setLoading(false)
    }
  }, [orgId, page, rowsPerPage])

  useEffect(() => {
    fetchLinksPage()
  }, [fetchLinksPage])

  const openDelete = (row) => {
    setDeleteTarget(row)
    setDeleteError(null)
    setDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      setDeleteLoading(true)
      setDeleteError(null)
      await storeLocationMappingService.remove(deleteTarget.id)
      setSuccessMessage('Location link removed')
      setTimeout(() => setSuccessMessage(null), 4000)
      setDeleteOpen(false)
      setDeleteTarget(null)
      fetchLinksPage()
    } catch (e) {
      setDeleteError(e.response?.data?.message || 'Delete failed')
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/store-location-mapping')}
          color="inherit"
        >
          Back to list
        </Button>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}>
            Store location mapping
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Shop Id #{orgId}: {orgName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setEditOpen(true)}
          disabled={loading || !!loadError || !Number.isFinite(orgId)}
          sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#cc0000' } }}
        >
          Edit locations
        </Button>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Location ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last modified</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : totalLinks === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No locations linked yet. Use Edit to add.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  linkItems.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.location_id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.location?.name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.location?.code ?? '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(row.date_modified)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove this link">
                          <IconButton
                            size="small"
                            onClick={() => openDelete(row)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && totalLinks > 0 && (
            <TablePagination
              component="div"
              count={totalLinks}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </CardContent>
      </Card>

      <EditOrgLocationsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        organizationId={orgId}
        organizationName={orgName}
        onSaved={(msg) => {
          setSuccessMessage(msg || 'Saved')
          setTimeout(() => setSuccessMessage(null), 4000)
          fetchLinksPage()
        }}
      />

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Remove this link?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Location:{' '}
            <strong>
              {deleteTarget?.location
                ? `${deleteTarget.location.name} (${deleteTarget.location.code})`
                : deleteTarget?.location_id ?? ''}
            </strong>
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  )
}

export default StoreLocationMappingOrgDetailPage
